import { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import db, { doc, collection, orderBy, query, addDoc, deleteDoc, updateDoc, serverTimestamp } from '../firebase/firestore';
import { subscribeCollection, subscribeDoc, subscribeWithRetry } from './useResilientSnapshot';
import { useAcademyPortalData } from '../contexts/academyPortalData';

export function useAcademy() {
  const shared = useAcademyPortalData();
  const own = useAcademyLive(!shared);
  return shared ? shared.academy : own;
}

export function useAcademyLive(enabled) {
  const { academyId } = useAuth();
  const [academy, setAcademy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    if (!academyId) { setLoading(false); return; }
    return subscribeDoc(doc(db, 'academies', academyId), setAcademy, setLoading);
  }, [enabled, academyId]);

  return { academy, loading };
}

export function useCustomFields(targetAcademyId) {
  const { academyId: ctxAcademyId } = useAuth();
  const aid = targetAcademyId || ctxAcademyId;
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!aid) { setLoading(false); return; }
    const q = query(
      collection(db, 'academies', aid, 'customFieldDefinitions'),
      orderBy('order', 'asc'),
    );
    return subscribeCollection(q, setFields, setLoading);
  }, [aid]);

  return { fields, loading };
}

// Live listener with retry, not a one-shot getDocs() — see useMyPlayer's
// comment (src/hooks/useGroups.js) for why: this drives hasPrograms in
// SelectedProgramContext, so a silent one-shot failure right after a fresh
// login could corrupt program filtering for the rest of the session with
// nothing retrying it.
export function usePrograms() {
  const { academyId } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!academyId) { setLoading(false); return undefined; }
    return subscribeWithRetry(collection(db, 'academies', academyId, 'programs'), (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => a.name.localeCompare(b.name));
      setPrograms(data);
      setLoading(false);
    }, { onGiveUp: () => setLoading(false) });
  }, [academyId]);

  const syncProgramCalendarEvent = async (programId, data, existingCalendarEventId) => {
    if (!data.startDate) return null;
    const startTs = Timestamp.fromDate(new Date(data.startDate + 'T09:00:00'));
    const endTs = data.endDate
      ? Timestamp.fromDate(new Date(data.endDate + 'T18:00:00'))
      : startTs;

    const eventData = {
      academyId,
      title: `${data.name} — Start`,
      type: 'programmeStart',
      start: startTs,
      end: endTs,
      allDay: true,
      linkedId: programId,
      linkedType: 'program',
      color: null,
      description: data.name,
      createdBy: 'system',
    };

    if (existingCalendarEventId) {
      await updateDoc(doc(db, 'calendarEvents', existingCalendarEventId), eventData);
      return existingCalendarEventId;
    } else {
      const ref = await addDoc(collection(db, 'calendarEvents'), { ...eventData, createdAt: serverTimestamp() });
      return ref.id;
    }
  };

  const addProgram = async (data) => {
    const ref = await addDoc(collection(db, 'academies', academyId, 'programs'), { ...data, createdAt: serverTimestamp() });
    const calendarEventId = await syncProgramCalendarEvent(ref.id, data, null);
    if (calendarEventId) {
      await updateDoc(doc(db, 'academies', academyId, 'programs', ref.id), { calendarEventId });
    }
    setPrograms((prev) => [...prev, { id: ref.id, ...data, calendarEventId }].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const updateProgram = async (id, data) => {
    const existing = programs.find((p) => p.id === id);
    const calendarEventId = await syncProgramCalendarEvent(id, data, existing?.calendarEventId || null);
    const updateData = calendarEventId ? { ...data, calendarEventId } : data;
    await updateDoc(doc(db, 'academies', academyId, 'programs', id), updateData);
    setPrograms((prev) => prev.map((p) => p.id === id ? { ...p, ...updateData } : p).sort((a, b) => a.name.localeCompare(b.name)));
  };

  const removeProgram = async (id) => {
    const existing = programs.find((p) => p.id === id);
    if (existing?.calendarEventId) {
      try { await deleteDoc(doc(db, 'calendarEvents', existing.calendarEventId)); } catch { /* ignore */ }
    }
    await deleteDoc(doc(db, 'academies', academyId, 'programs', id));
    setPrograms((prev) => prev.filter((p) => p.id !== id));
  };

  return { programs, loading, addProgram, updateProgram, removeProgram };
}
